import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getTrades } from '../services/firestoreService';
import { usePlan } from '../hooks/usePlan';
import UpgradePrompt from '../components/UpgradePrompt';
import './Reports.css';

// ── Static chart colours (readable on dark & light backgrounds) ─────────────
const C = {
  profit:  '#3fb950',
  loss:    '#f85149',
  blue:    '#58a6ff',
  orange:  '#d29922',
  purple:  '#a371f7',
  muted:   '#8b949e',
};

// ── Formatters ───────────────────────────────────────────────────────────────
const fmt = n => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(n ?? 0));
const sign = n => {
  if (n === undefined || n === null || isNaN(n)) return '₹0';
  return n >= 0 ? `+₹${fmt(n)}` : `-₹${fmt(n)}`;
};

// ── Calculation helpers ──────────────────────────────────────────────────────
const calcWinRate = ts => {
  if (!ts.length) return 0;
  return (ts.filter(t => t.pnl > 0).length / ts.length) * 100;
};

const calcProfitFactor = ts => {
  const gp = ts.filter(t => t.pnl > 0).reduce((s, t) => s + t.pnl, 0);
  const gl = Math.abs(ts.filter(t => t.pnl < 0).reduce((s, t) => s + t.pnl, 0));
  return gl === 0 ? (gp > 0 ? Infinity : 0) : gp / gl;
};

const calcMaxDrawdown = ts => {
  let peak = 0, maxDD = 0, running = 0;
  ts.forEach(t => {
    running += t.pnl || 0;
    if (running > peak) peak = running;
    const dd = peak - running;
    if (dd > maxDD) maxDD = dd;
  });
  return maxDD;
};

const calcStreaks = ts => {
  let bw = 0, bl = 0, cw = 0, cl = 0;
  ts.forEach(t => {
    if ((t.pnl || 0) > 0) { cw++; cl = 0; if (cw > bw) bw = cw; }
    else                   { cl++; cw = 0; if (cl > bl) bl = cl; }
  });
  return { bestWin: bw, bestLoss: bl };
};

const DISCIPLINE_BAD = ['FOMO', 'Revenge', 'Greedy', 'Fearful', 'Anxious'];
const calcDiscipline = ts => {
  if (!ts.length) return 0;
  return Math.round((ts.filter(t => !DISCIPLINE_BAD.includes(t.emotion)).length / ts.length) * 100);
};

const monthKey = date => {
  const d = new Date(date);
  return `${d.toLocaleString('default', { month: 'short' })} '${String(d.getFullYear()).slice(2)}`;
};

// ── Weekly / Monthly helpers ─────────────────────────────────────────────────
const getWeekBounds = (weekOffset = 0) => {
  const now = new Date();
  const dow = now.getDay(); // 0=Sun
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1) + weekOffset * 7);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { monday, sunday };
};

const getWeekTrades = (trades, weekOffset = 0) => {
  const { monday, sunday } = getWeekBounds(weekOffset);
  return trades.filter(t => {
    const d = new Date(t.date);
    return d >= monday && d <= sunday;
  });
};

const getMonthTrades = (trades, year, month) =>
  trades.filter(t => {
    const d = new Date(t.date);
    return d.getFullYear() === year && d.getMonth() === month;
  });

const calcStats = tradeArr => {
  if (!tradeArr.length)
    return { pnl: 0, winRate: 0, trades: 0, wins: 0, losses: 0,
             profitFactor: 0, avgWin: 0, avgLoss: 0, bestTrade: 0, worstTrade: 0 };
  const wins   = tradeArr.filter(t => (t.pnl || 0) > 0);
  const losses = tradeArr.filter(t => (t.pnl || 0) < 0);
  const gp = wins.reduce((s, t) => s + t.pnl, 0);
  const gl = Math.abs(losses.reduce((s, t) => s + t.pnl, 0));
  return {
    pnl:          tradeArr.reduce((s, t) => s + (t.pnl || 0), 0),
    winRate:      (wins.length / tradeArr.length) * 100,
    trades:       tradeArr.length,
    wins:         wins.length,
    losses:       losses.length,
    profitFactor: gl === 0 ? gp : gp / gl,
    avgWin:       wins.length   ? gp / wins.length   : 0,
    avgLoss:      losses.length ? gl / losses.length : 0,
    bestTrade:    Math.max(...tradeArr.map(t => t.pnl || 0), 0),
    worstTrade:   Math.min(...tradeArr.map(t => t.pnl || 0), 0),
  };
};

const calcChange = (curr, prev) => {
  if (!prev) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

const groupByDayOfWeek = tradeArr => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  return days.map((day, i) => ({
    day,
    pnl: tradeArr
      .filter(t => new Date(t.date).getDay() === i + 1)
      .reduce((s, t) => s + (t.pnl || 0), 0),
  }));
};

// Change badge helper
const ChangeBadge = ({ curr, prev, invertSign = false }) => {
  const ch = calcChange(curr, prev);
  if (ch === null || isNaN(ch)) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>;
  const good = invertSign ? ch <= 0 : ch >= 0;
  return (
    <span className={`change-badge ${good ? 'positive' : 'negative'}`}>
      {ch >= 0 ? '↑' : '↓'} {Math.abs(ch).toFixed(1)}%
    </span>
  );
};

// ── Custom Recharts tooltip ──────────────────────────────────────────────────
const Tip = ({ active, payload, label, pnlFormat = true }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rp-tooltip">
      {label && <div className="rp-tooltip-label">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || 'var(--text-primary)', fontWeight: 600 }}>
          {p.name}:{' '}
          {pnlFormat && typeof p.value === 'number' ? sign(p.value) : p.value}
        </div>
      ))}
    </div>
  );
};

// ── Shared card components ───────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color }) => (
  <div className="rp-stat-card">
    <div className="rp-stat-label">{label}</div>
    <div className="rp-stat-value" style={{ color }}>{value}</div>
    {sub && <div className="rp-stat-sub">{sub}</div>}
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="rp-chart-card">
    <div className="rp-chart-title">{title}</div>
    {children}
  </div>
);

// ── CSV export ───────────────────────────────────────────────────────────────
const exportCSV = (headers, rows, filename) => {
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const TABS = [
  { id: 'overview', label: 'Overview',        feature: 'reportsOverview'   },
  { id: 'weekly',   label: 'Weekly Report',   feature: 'reportsWeekly'     },
  { id: 'monthly',  label: 'Monthly Report',  feature: 'reportsMonthly'    },
  { id: 'perf',     label: 'Performance',     feature: 'reportsPerformance'},
  { id: 'psych',    label: 'Psychology',      feature: 'reportsPsychology' },
  { id: 'risk',     label: 'Risk Report',     feature: 'reportsRisk'       },
  { id: 'symbols',  label: 'Symbol Analysis', feature: 'reportsSymbol'     },
  { id: 'export',   label: 'Export',          feature: 'reportsExport'     },
];

// ════════════════════════════════════════════════════════════════════════════
const Reports = ({ currentUser }) => {
  const [tab, setTab]             = useState('overview');
  const [trades, setTrades]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [weekOffset, setWeekOffset]   = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const { can } = usePlan();

  useEffect(() => {
    if (!currentUser?.id) return;
    setLoading(true);
    getTrades(currentUser.id).then(ts => {
      setTrades(ts.slice().sort((a, b) => new Date(a.date) - new Date(b.date)));
      setLoading(false);
    });
  }, [currentUser]);

  // ── All derived metrics in one useMemo ────────────────────────────────────
  const d = useMemo(() => {
    if (!trades.length) return null;

    const totalPnl = trades.reduce((s, t) => s + (t.pnl || 0), 0);
    const wins     = trades.filter(t => (t.pnl || 0) > 0);
    const losses   = trades.filter(t => (t.pnl || 0) < 0);
    const winRate  = calcWinRate(trades);
    const pf       = calcProfitFactor(trades);

    // Best / worst day
    const byDay = {};
    trades.forEach(t => { byDay[t.date] = (byDay[t.date] || 0) + (t.pnl || 0); });
    const dayVals = Object.values(byDay);
    const bestDay  = dayVals.length ? Math.max(...dayVals) : 0;
    const worstDay = dayVals.length ? Math.min(...dayVals) : 0;

    // Monthly P&L
    const monthMap = {};
    trades.forEach(t => {
      const k = monthKey(t.date);
      monthMap[k] = (monthMap[k] || 0) + (t.pnl || 0);
    });
    const monthlyData = Object.entries(monthMap).map(([name, pnl]) => ({ name, pnl }));

    // Cumulative P&L
    let cum = 0;
    const cumulativeData = trades.map(t => {
      cum += t.pnl || 0;
      return { date: t.date, pnl: Math.round(cum) };
    });

    // Avg / largest win/loss
    const avgWin     = wins.length  ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length   : 0;
    const avgLoss    = losses.length ? losses.reduce((s, t) => s + t.pnl, 0) / losses.length : 0;
    const largestWin  = wins.length  ? Math.max(...wins.map(t => t.pnl))   : 0;
    const largestLoss = losses.length ? Math.min(...losses.map(t => t.pnl)) : 0;
    const { bestWin, bestLoss } = calcStreaks(trades);

    // Day-of-week P&L
    const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    const dowMap = {}; const dowCnt = {};
    DOW.forEach(d2 => { dowMap[d2] = 0; dowCnt[d2] = 0; });
    trades.forEach(t => {
      const day = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(t.date).getDay()];
      if (dowMap[day] !== undefined) { dowMap[day] += t.pnl || 0; dowCnt[day]++; }
    });
    const dowData = DOW.map(day => ({ name: day, pnl: Math.round(dowMap[day]), trades: dowCnt[day] }));

    // Time-of-day
    const SLOTS = [
      { name: 'Morning (9-11am)',   test: h => h >= 9  && h < 11 },
      { name: 'Midday (11am-1pm)',  test: h => h >= 11 && h < 13 },
      { name: 'Afternoon (1-3pm)', test: h => h >= 13 && h < 15 },
      { name: 'Close (3-3:30pm)',   test: h => h >= 15 },
    ];
    const todData = SLOTS.map(slot => {
      const sl = trades.filter(t => {
        if (!t.time) return false;
        return slot.test(parseInt(t.time.split(':')[0], 10));
      });
      return { name: slot.name, pnl: Math.round(sl.reduce((s, t) => s + (t.pnl || 0), 0)), trades: sl.length };
    });

    // P&L distribution (histogram)
    const avgAbsLoss = losses.length ? Math.abs(avgLoss) : 1000;
    const bucket = (pnl) => {
      const r = pnl / avgAbsLoss;
      if (r <= -2)      return '≤ -2R';
      if (r <= -1)      return '-2R to -1R';
      if (r < 0)        return '-1R to 0';
      if (r < 1)        return '0 to +1R';
      if (r < 2)        return '+1R to +2R';
      return '≥ +2R';
    };
    const bucketOrder = ['≤ -2R', '-2R to -1R', '-1R to 0', '0 to +1R', '+1R to +2R', '≥ +2R'];
    const bucketMap = {};
    bucketOrder.forEach(b => { bucketMap[b] = 0; });
    trades.forEach(t => { const b = bucket(t.pnl || 0); bucketMap[b]++; });
    const rMultipleData = bucketOrder.map(name => ({ name, count: bucketMap[name] }));

    // Emotion data
    const emotionMap = {};
    trades.forEach(t => {
      const e = t.emotion || 'Neutral';
      if (!emotionMap[e]) emotionMap[e] = { pnl: 0, count: 0 };
      emotionMap[e].pnl += t.pnl || 0;
      emotionMap[e].count++;
    });
    const emotionData = Object.entries(emotionMap)
      .map(([emotion, v]) => ({ emotion, avgPnl: Math.round(v.pnl / v.count), count: v.count }))
      .sort((a, b) => b.avgPnl - a.avgPnl);

    // Setup data
    const setupMap = {};
    trades.forEach(t => {
      const s = (t.setup || 'Unknown').trim() || 'Unknown';
      if (!setupMap[s]) setupMap[s] = { wins: 0, total: 0 };
      setupMap[s].total++;
      if ((t.pnl || 0) > 0) setupMap[s].wins++;
    });
    const setupData = Object.entries(setupMap)
      .map(([setup, v]) => ({ setup, winRate: parseFloat(((v.wins / v.total) * 100).toFixed(1)), total: v.total }))
      .sort((a, b) => b.winRate - a.winRate);

    // Psychology
    const fomoTrades     = trades.filter(t => t.emotion === 'FOMO');
    const revengeTrades  = trades.filter(t => t.emotion === 'Revenge');
    const calmTrades     = trades.filter(t => t.emotion === 'Calm' || t.emotion === 'Confident');
    const emotionalTr    = trades.filter(t => DISCIPLINE_BAD.includes(t.emotion));
    const calmWR         = calcWinRate(calmTrades);
    const emotionWR      = calcWinRate(emotionalTr);
    const discipline     = calcDiscipline(trades);

    const mDiscipline = {};
    trades.forEach(t => {
      const k = monthKey(t.date);
      if (!mDiscipline[k]) mDiscipline[k] = { good: 0, total: 0 };
      mDiscipline[k].total++;
      if (!DISCIPLINE_BAD.includes(t.emotion)) mDiscipline[k].good++;
    });
    const monthlyDiscipline = Object.entries(mDiscipline)
      .map(([name, v]) => ({ name, score: Math.round((v.good / v.total) * 100) }));

    // Risk / drawdown
    let peak = 0, runEq = 0;
    const ddData = trades.map(t => {
      runEq += t.pnl || 0;
      if (runEq > peak) peak = runEq;
      return { date: t.date, equity: Math.round(runEq), drawdown: -Math.round(peak - runEq) };
    });
    const maxDD      = calcMaxDrawdown(trades);
    const lastEquity = ddData.length ? ddData[ddData.length - 1].equity : 0;
    const peakEquity = Math.max(...ddData.map(d2 => d2.equity), 0);
    const currentDD  = Math.max(0, peakEquity - lastEquity);

    let maxConLoss = 0, curConLoss = 0;
    trades.forEach(t => {
      if ((t.pnl || 0) < 0) { curConLoss++; if (curConLoss > maxConLoss) maxConLoss = curConLoss; }
      else curConLoss = 0;
    });

    // Symbol analysis
    const symMap = {};
    trades.forEach(t => {
      const sym = ((t.stock || t.symbol || 'Unknown')).toUpperCase().trim();
      if (!symMap[sym]) symMap[sym] = { pnl: 0, wins: 0, trades: [] };
      symMap[sym].pnl += t.pnl || 0;
      symMap[sym].trades.push(t);
      if ((t.pnl || 0) > 0) symMap[sym].wins++;
    });
    const symbolData = Object.entries(symMap).map(([symbol, v]) => {
      const wr   = parseFloat(((v.wins / v.trades.length) * 100).toFixed(1));
      const pnls = v.trades.map(t => t.pnl || 0);
      return {
        symbol, total: v.trades.length, winRate: wr,
        totalPnl: Math.round(v.pnl),
        avgPnl: Math.round(v.pnl / v.trades.length),
        bestTrade: Math.max(...pnls),
        worstTrade: Math.min(...pnls),
        rec: wr >= 60 ? 'Strong' : wr <= 40 ? 'Avoid' : 'Neutral',
      };
    }).sort((a, b) => b.totalPnl - a.totalPnl);

    return {
      totalPnl, winRate, pf, bestDay, worstDay,
      monthlyData, cumulativeData,
      avgWin, avgLoss, largestWin, largestLoss, bestWin, bestLoss,
      dowData, todData, rMultipleData,
      emotionData, setupData,
      fomoTrades, revengeTrades, calmTrades, emotionalTr,
      calmWR, emotionWR, discipline, monthlyDiscipline,
      maxDD, currentDD, maxConLoss, ddData,
      symbolData,
    };
  }, [trades]);

  // ── Loading / empty states ────────────────────────────────────────────────
  if (loading) return (
    <div className="rp-loading">
      <div className="rp-spinner" />
      <p style={{ color: 'var(--text-secondary)' }}>Loading reports…</p>
    </div>
  );

  if (!trades.length || !d) return (
    <div className="rp-empty">
      <p>No trades logged yet. Start trading to see your reports.</p>
    </div>
  );

  // ── TAB 1: Overview ───────────────────────────────────────────────────────
  const renderOverview = () => (
    <div className="rp-section">
      <div className="rp-stat-grid">
        <StatCard label="Total P&L"     value={sign(d.totalPnl)}          color={d.totalPnl >= 0 ? C.profit : C.loss}   />
        <StatCard label="Win Rate"      value={`${d.winRate.toFixed(1)}%`} color={d.winRate >= 50  ? C.profit : C.loss}  />
        <StatCard label="Profit Factor" value={isFinite(d.pf) ? d.pf.toFixed(2) : '∞'} color={d.pf >= 1 ? C.profit : C.loss} />
        <StatCard label="Total Trades"  value={trades.length}              color={C.blue}                                />
        <StatCard label="Best Day"      value={sign(d.bestDay)}            color={C.profit}                              />
        <StatCard label="Worst Day"     value={sign(d.worstDay)}           color={C.loss}                                />
      </div>

      <ChartCard title="Monthly P&L">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={d.monthlyData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
            <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
            <Tooltip content={<Tip />} />
            <Bar dataKey="pnl" name="P&L" radius={[4, 4, 0, 0]}>
              {d.monthlyData.map((entry, i) => (
                <Cell key={i} fill={entry.pnl >= 0 ? C.profit : C.loss} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Cumulative P&L">
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={d.cumulativeData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="date" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
            <Tooltip content={<Tip />} />
            <Line type="monotone" dataKey="pnl" name="Cumulative P&L" stroke={C.profit} dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );

  // ── TAB 2: Performance ────────────────────────────────────────────────────
  const renderPerformance = () => (
    <div className="rp-section">
      <div className="rp-stat-grid">
        <StatCard label="Avg Win"       value={sign(d.avgWin)}            color={C.profit}                                   />
        <StatCard label="Avg Loss"      value={sign(d.avgLoss)}           color={C.loss}                                     />
        <StatCard label="Largest Win"   value={sign(d.largestWin)}        color={C.profit}                                   />
        <StatCard label="Largest Loss"  value={sign(d.largestLoss)}       color={C.loss}                                     />
        <StatCard label="Best Streak"   value={`${d.bestWin} wins`}       color={C.profit}                                   />
        <StatCard label="Worst Streak"  value={`${d.bestLoss} losses`}    color={C.loss}                                     />
      </div>

      <div className="rp-chart-row">
        <ChartCard title="Win vs Loss">
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Wins',   value: trades.filter(t => (t.pnl || 0) > 0).length  },
                  { name: 'Losses', value: trades.filter(t => (t.pnl || 0) <= 0).length },
                ]}
                cx="50%" cy="50%"
                innerRadius={55} outerRadius={85}
                paddingAngle={3} dataKey="value"
              >
                <Cell fill={C.profit} />
                <Cell fill={C.loss}   />
              </Pie>
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="rp-pie-legend">
            <span style={{ color: C.profit }}>● Wins: {trades.filter(t => (t.pnl || 0) > 0).length}</span>
            <span style={{ color: C.loss   }}>● Losses: {trades.filter(t => (t.pnl || 0) <= 0).length}</span>
          </div>
        </ChartCard>

        <ChartCard title="Day of Week P&L">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={d.dowData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
              <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
              <Tooltip content={<Tip />} />
              <Bar dataKey="pnl" name="P&L" radius={[4, 4, 0, 0]}>
                {d.dowData.map((entry, i) => <Cell key={i} fill={entry.pnl >= 0 ? C.profit : C.loss} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="Time of Day Performance">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={d.todData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
            <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
            <Tooltip content={<Tip />} />
            <Bar dataKey="pnl" name="P&L" radius={[4, 4, 0, 0]}>
              {d.todData.map((entry, i) => <Cell key={i} fill={entry.pnl >= 0 ? C.profit : C.loss} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="R-Multiple Distribution">
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Trades bucketed by P&L relative to your average loss size
        </p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={d.rMultipleData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
            <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }}
              formatter={v => [`${v} trades`, 'Count']}
            />
            <Bar dataKey="count" name="Trades" radius={[4, 4, 0, 0]}>
              {d.rMultipleData.map((entry, i) => (
                <Cell key={i} fill={i >= 3 ? C.profit : C.loss} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );

  // ── TAB 3: Psychology ─────────────────────────────────────────────────────
  const renderPsychology = () => (
    <div className="rp-section">
      <div className="rp-stat-grid">
        <StatCard
          label="FOMO Trades" value={d.fomoTrades.length}
          sub={d.fomoTrades.length ? `P&L: ${sign(d.fomoTrades.reduce((s, t) => s + (t.pnl || 0), 0))}` : '—'}
          color={C.orange}
        />
        <StatCard
          label="Revenge Trades" value={d.revengeTrades.length}
          sub={d.revengeTrades.length ? `P&L: ${sign(d.revengeTrades.reduce((s, t) => s + (t.pnl || 0), 0))}` : '—'}
          color={C.loss}
        />
        <StatCard label="Calm Win Rate"      value={`${d.calmWR.toFixed(1)}%`}    color={C.profit}                                    />
        <StatCard label="Emotional Win Rate" value={`${d.emotionWR.toFixed(1)}%`} color={d.emotionWR >= 50 ? C.profit : C.loss}        />
        <StatCard label="Discipline Score"   value={`${d.discipline}/100`}        color={d.discipline >= 70 ? C.profit : d.discipline >= 40 ? C.orange : C.loss} />
        <StatCard label="Emotional Trades"   value={d.emotionalTr.length}         color={C.orange}                                    />
      </div>

      <ChartCard title="Average P&L by Emotion">
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={d.emotionData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="emotion" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
            <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
            <Tooltip content={<Tip />} />
            <Bar dataKey="avgPnl" name="Avg P&L" radius={[4, 4, 0, 0]}>
              {d.emotionData.map((e, i) => <Cell key={i} fill={e.avgPnl >= 0 ? C.profit : C.loss} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="rp-chart-row">
        <ChartCard title="Setup Win Rate">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.setupData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="setup" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `${v}%`} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }}
                formatter={v => [`${v}%`, 'Win Rate']}
              />
              <Bar dataKey="winRate" name="Win Rate" radius={[4, 4, 0, 0]}>
                {d.setupData.map((s, i) => <Cell key={i} fill={s.winRate >= 50 ? C.profit : C.loss} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Monthly Discipline Score">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.monthlyDiscipline} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }}
                formatter={v => [`${v}/100`, 'Discipline']}
              />
              <Line type="monotone" dataKey="score" name="Discipline" stroke={C.blue} strokeWidth={2} dot={{ fill: C.blue, r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );

  // ── TAB 4: Risk Report ────────────────────────────────────────────────────
  const renderRisk = () => (
    <div className="rp-section">
      <div className="rp-stat-grid">
        <StatCard label="Max Drawdown"         value={`₹${fmt(d.maxDD)}`}          color={C.loss}                                    />
        <StatCard label="Current Drawdown"     value={`₹${fmt(d.currentDD)}`}      color={d.currentDD > 0 ? C.orange : C.profit}     />
        <StatCard label="Max Consec. Losses"   value={d.maxConLoss}                 color={d.maxConLoss >= 3 ? C.loss : C.orange}     />
        <StatCard label="Total P&L"            value={sign(d.totalPnl)}             color={d.totalPnl >= 0 ? C.profit : C.loss}       />
        <StatCard label="Profit Factor"        value={isFinite(d.pf) ? d.pf.toFixed(2) : '∞'} color={d.pf >= 1 ? C.profit : C.loss} />
        <StatCard label="Win Rate"             value={`${d.winRate.toFixed(1)}%`}   color={d.winRate >= 50 ? C.profit : C.loss}       />
      </div>

      <ChartCard title="Equity Curve & Drawdown">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={d.ddData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="date" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
            <Tooltip content={<Tip />} />
            <Line type="monotone" dataKey="equity"   name="Equity P&L" stroke={C.profit} dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="drawdown" name="Drawdown"    stroke={C.loss}   dot={false} strokeWidth={1.5} strokeDasharray="5 3" />
          </LineChart>
        </ResponsiveContainer>
        <div className="rp-chart-legend">
          <span style={{ color: C.profit }}>─ Equity Curve</span>
          <span style={{ color: C.loss }}>╌ Drawdown</span>
        </div>
      </ChartCard>
    </div>
  );

  // ── TAB 5: Symbol Analysis ────────────────────────────────────────────────
  const renderSymbols = () => {
    const sorted     = d.symbolData;
    const top3Best   = [...sorted].sort((a, b) => b.totalPnl - a.totalPnl).slice(0, 3);
    const top3Worst  = [...sorted].sort((a, b) => a.totalPnl - b.totalPnl).slice(0, 3);
    return (
      <div className="rp-section">
        <div className="rp-sym-row">
          <div className="rp-sym-group">
            <h3 className="rp-sub-title" style={{ color: C.profit }}>Top 3 Best</h3>
            <div className="rp-mini-cards">
              {top3Best.map(s => (
                <div key={s.symbol} className="rp-mini-card rp-mini-profit">
                  <div className="rp-mini-sym">{s.symbol}</div>
                  <div style={{ color: C.profit, fontWeight: 700 }}>{sign(s.totalPnl)}</div>
                  <div className="rp-mini-meta">WR {s.winRate}% · {s.total} trades</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rp-sym-group">
            <h3 className="rp-sub-title" style={{ color: C.loss }}>Top 3 Worst</h3>
            <div className="rp-mini-cards">
              {top3Worst.map(s => (
                <div key={s.symbol} className="rp-mini-card rp-mini-loss">
                  <div className="rp-mini-sym">{s.symbol}</div>
                  <div style={{ color: C.loss, fontWeight: 700 }}>{sign(s.totalPnl)}</div>
                  <div className="rp-mini-meta">WR {s.winRate}% · {s.total} trades</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rp-table-wrap">
          <table className="rp-table">
            <thead>
              <tr>
                <th>Symbol</th><th>Trades</th><th>Win Rate</th>
                <th>Total P&L</th><th>Avg P&L</th>
                <th>Best</th><th>Worst</th><th>Signal</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => (
                <tr key={s.symbol}>
                  <td className="rp-sym-name">{s.symbol}</td>
                  <td>{s.total}</td>
                  <td style={{ color: s.winRate >= 50 ? C.profit : C.loss }}>{s.winRate}%</td>
                  <td style={{ color: s.totalPnl >= 0 ? C.profit : C.loss }}>{sign(s.totalPnl)}</td>
                  <td style={{ color: s.avgPnl >= 0 ? C.profit : C.loss }}>{sign(s.avgPnl)}</td>
                  <td style={{ color: C.profit }}>{sign(s.bestTrade)}</td>
                  <td style={{ color: C.loss   }}>{sign(s.worstTrade)}</td>
                  <td><span className={`rp-rec rp-rec-${s.rec.toLowerCase()}`}>{s.rec}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ── TAB 6: Export ─────────────────────────────────────────────────────────
  const renderExport = () => (
    <div className="rp-section">
      <div className="rp-export-grid">
        <div className="rp-export-card">
          <h3>All Trades CSV</h3>
          <p>Every trade with full details — date, symbol, entry/exit, P&amp;L, emotion, setup.</p>
          <button className="rp-btn" onClick={() => {
            exportCSV(
              ['Date', 'Symbol', 'Type', 'Entry', 'Exit', 'Qty', 'P&L', 'Emotion', 'Setup', 'Notes'],
              trades.map(t => [
                t.date, t.stock || t.symbol || '', t.type || '',
                t.entry || '', t.exit || '', t.quantity || '',
                t.pnl || 0, t.emotion || '', t.setup || '', t.notes || '',
              ]),
              `tradefix-trades-${new Date().toISOString().split('T')[0]}.csv`
            );
          }}>
            Download Trades CSV
          </button>
        </div>

        <div className="rp-export-card">
          <h3>Monthly Summary CSV</h3>
          <p>Month-by-month totals — trades, win rate, P&amp;L, best day.</p>
          <button className="rp-btn" onClick={() => {
            exportCSV(
              ['Month', 'Trades', 'Win Rate', 'Total P&L', 'Best Day'],
              d.monthlyData.map(m => {
                const mTr = trades.filter(t => monthKey(t.date) === m.name);
                const wr  = mTr.length ? ((mTr.filter(t => (t.pnl || 0) > 0).length / mTr.length) * 100).toFixed(1) : '0.0';
                const dMap = {}; mTr.forEach(t => { dMap[t.date] = (dMap[t.date] || 0) + (t.pnl || 0); });
                const bestD = Object.values(dMap).length ? Math.max(...Object.values(dMap)) : 0;
                return [m.name, mTr.length, `${wr}%`, m.pnl.toFixed(2), bestD.toFixed(2)];
              }),
              `tradefix-monthly-${new Date().toISOString().split('T')[0]}.csv`
            );
          }}>
            Download Monthly CSV
          </button>
        </div>

        <div className="rp-export-card">
          <h3>Print / Save PDF</h3>
          <p>Open your browser's print dialog to save this page as a PDF.</p>
          <button className="rp-btn rp-btn-outline" onClick={() => window.print()}>
            Print Report
          </button>
        </div>
      </div>

      <div className="rp-summary-box">
        <h3 style={{ margin: '0 0 16px', color: 'var(--text-primary)' }}>Quick Summary</h3>
        <div className="rp-summary-grid">
          <div><span className="rp-sum-label">Total Trades</span>       <span style={{ color: 'var(--text-primary)' }}>{trades.length}</span></div>
          <div><span className="rp-sum-label">Total P&L</span>          <span style={{ color: d.totalPnl >= 0 ? C.profit : C.loss }}>{sign(d.totalPnl)}</span></div>
          <div><span className="rp-sum-label">Win Rate</span>           <span style={{ color: 'var(--text-primary)' }}>{d.winRate.toFixed(1)}%</span></div>
          <div><span className="rp-sum-label">Profit Factor</span>      <span style={{ color: 'var(--text-primary)' }}>{isFinite(d.pf) ? d.pf.toFixed(2) : '∞'}</span></div>
          <div><span className="rp-sum-label">Max Drawdown</span>       <span style={{ color: C.loss }}>₹{fmt(d.maxDD)}</span></div>
          <div><span className="rp-sum-label">Discipline Score</span>   <span style={{ color: 'var(--text-primary)' }}>{d.discipline}/100</span></div>
          <div><span className="rp-sum-label">Best Streak</span>        <span style={{ color: C.profit }}>{d.bestWin} wins</span></div>
          <div><span className="rp-sum-label">Worst Streak</span>       <span style={{ color: C.loss }}>{d.bestLoss} losses</span></div>
        </div>
      </div>
    </div>
  );

  // ── TAB: Weekly Comparison ───────────────────────────────────────────────
  const renderWeekly = () => {
    const { monday, sunday } = getWeekBounds(weekOffset);
    const fmt2 = d => d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
    const weekLabel = `Week of ${fmt2(monday)} – ${fmt2(sunday)}, ${sunday.getFullYear()}`;

    const twTrades = getWeekTrades(trades, weekOffset);
    const lwTrades = getWeekTrades(trades, weekOffset - 1);
    const tw = calcStats(twTrades);
    const lw = calcStats(lwTrades);

    // Day by day chart
    const dowThis = groupByDayOfWeek(twTrades);
    const dowLast = groupByDayOfWeek(lwTrades);
    const dowChartData = dowThis.map((e, i) => ({
      day: e.day,
      'This Week': Math.round(e.pnl),
      'Last Week': Math.round(dowLast[i].pnl),
    }));

    // Best / worst day
    const byDay = {};
    twTrades.forEach(t => { byDay[t.date] = (byDay[t.date] || 0) + (t.pnl || 0); });
    const dayEntries = Object.entries(byDay);
    const bestDay  = dayEntries.length ? dayEntries.reduce((a, b) => b[1] > a[1] ? b : a) : null;
    const worstDay = dayEntries.length ? dayEntries.reduce((a, b) => b[1] < a[1] ? b : a) : null;
    const getDayName = dateStr => new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'long' });

    // Psychology
    const emotionCount = (arr, em) => arr.filter(t => t.emotion === em).length;
    const topEmotion = arr => {
      if (!arr.length) return '—';
      const m = {}; arr.forEach(t => { m[t.emotion || 'Neutral'] = (m[t.emotion || 'Neutral'] || 0) + 1; });
      return Object.entries(m).sort((a,b) => b[1]-a[1])[0][0];
    };

    const metrics = [
      { label: 'Total P&L',           twVal: sign(tw.pnl),   lwVal: sign(lw.pnl),   curr: tw.pnl,   prev: lw.pnl,  twColor: tw.pnl >= 0 ? C.profit : C.loss },
      { label: 'Win Rate',            twVal: `${tw.winRate.toFixed(1)}%`,  lwVal: `${lw.winRate.toFixed(1)}%`,  curr: tw.winRate, prev: lw.winRate },
      { label: 'Total Trades',        twVal: tw.trades, lwVal: lw.trades, curr: tw.trades, prev: lw.trades },
      { label: 'Wins / Losses',       twVal: `${tw.wins} / ${tw.losses}`, lwVal: `${lw.wins} / ${lw.losses}`, curr: null, prev: null },
      { label: 'Profit Factor',       twVal: isFinite(tw.profitFactor) ? tw.profitFactor.toFixed(2) : '∞', lwVal: isFinite(lw.profitFactor) ? lw.profitFactor.toFixed(2) : '∞', curr: isFinite(tw.profitFactor) ? tw.profitFactor : 0, prev: isFinite(lw.profitFactor) ? lw.profitFactor : 0 },
      { label: 'Avg Win',             twVal: sign(tw.avgWin),  lwVal: sign(lw.avgWin),  curr: tw.avgWin,  prev: lw.avgWin },
      { label: 'Avg Loss',            twVal: `₹${fmt(tw.avgLoss)}`, lwVal: `₹${fmt(lw.avgLoss)}`, curr: -tw.avgLoss, prev: -lw.avgLoss },
      { label: 'Best Trade',          twVal: sign(tw.bestTrade),   lwVal: sign(lw.bestTrade),   curr: tw.bestTrade,  prev: lw.bestTrade },
      { label: 'Worst Trade',         twVal: sign(tw.worstTrade),  lwVal: sign(lw.worstTrade),  curr: tw.worstTrade, prev: lw.worstTrade, invertSign: true },
      { label: 'Max Consec. Wins',    twVal: calcStreaks(twTrades).bestWin,  lwVal: calcStreaks(lwTrades).bestWin,  curr: calcStreaks(twTrades).bestWin,  prev: calcStreaks(lwTrades).bestWin },
      { label: 'Max Consec. Losses',  twVal: calcStreaks(twTrades).bestLoss, lwVal: calcStreaks(lwTrades).bestLoss, curr: -calcStreaks(twTrades).bestLoss, prev: -calcStreaks(lwTrades).bestLoss, invertSign: true },
    ];

    return (
      <div className="rp-section">
        {/* Week Selector */}
        <div className="week-selector">
          <button onClick={() => setWeekOffset(o => o - 1)}>◀ Prev</button>
          <span className="week-label">{weekLabel}</span>
          <button onClick={() => setWeekOffset(o => Math.min(o + 1, 0))} disabled={weekOffset >= 0} style={{ opacity: weekOffset >= 0 ? 0.4 : 1 }}>Next ▶</button>
        </div>

        {/* Comparison Cards */}
        <div className="comparison-grid">
          <div className="comparison-card current">
            <div className="comparison-card-header">This Week</div>
            {[
              { label: 'Total P&L',      val: sign(tw.pnl),      color: tw.pnl >= 0 ? C.profit : C.loss },
              { label: 'Total Trades',   val: tw.trades },
              { label: 'Win Rate',       val: `${tw.winRate.toFixed(1)}%`, color: tw.winRate >= 50 ? C.profit : C.loss },
              { label: 'Profit Factor',  val: isFinite(tw.profitFactor) ? tw.profitFactor.toFixed(2) : '∞' },
              { label: 'Best Day',       val: bestDay  ? `${getDayName(bestDay[0])} · ${sign(bestDay[1])}` : '—', color: C.profit },
              { label: 'Worst Day',      val: worstDay ? `${getDayName(worstDay[0])} · ${sign(worstDay[1])}` : '—', color: C.loss },
              { label: 'Avg Trade P&L',  val: tw.trades ? sign(tw.pnl / tw.trades) : '—' },
            ].map(m => (
              <div key={m.label} className="comparison-metric">
                <span className="metric-label">{m.label}</span>
                <span className="metric-value" style={{ color: m.color || 'var(--text-primary)' }}>{m.val}</span>
              </div>
            ))}
          </div>

          <div className="comparison-card">
            <div className="comparison-card-header">Last Week</div>
            {[
              { label: 'Total P&L',      val: sign(lw.pnl),      color: lw.pnl >= 0 ? C.profit : C.loss },
              { label: 'Total Trades',   val: lw.trades },
              { label: 'Win Rate',       val: `${lw.winRate.toFixed(1)}%`, color: lw.winRate >= 50 ? C.profit : C.loss },
              { label: 'Profit Factor',  val: isFinite(lw.profitFactor) ? lw.profitFactor.toFixed(2) : '∞' },
              { label: 'Avg Trade P&L',  val: lw.trades ? sign(lw.pnl / lw.trades) : '—' },
            ].map(m => (
              <div key={m.label} className="comparison-metric">
                <span className="metric-label">{m.label}</span>
                <span className="metric-value" style={{ color: m.color || 'var(--text-primary)' }}>{m.val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Day-by-Day Comparison Chart */}
        <ChartCard title="Day by Day P&L — This Week vs Last Week">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dowChartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="day" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
              <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
              <Tooltip content={<Tip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }} />
              <Bar dataKey="This Week" fill={C.profit} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Last Week" fill={C.muted}  radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Metrics Table */}
        <ChartCard title="Weekly Metrics Comparison">
          <div style={{ overflowX: 'auto' }}>
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>This Week</th>
                  <th>Last Week</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((m, i) => (
                  <tr key={m.label} style={{ background: i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-primary)' }}>
                    <td style={{ color: 'var(--text-secondary)' }}>{m.label}</td>
                    <td style={{ fontWeight: 700, color: m.twColor || 'var(--text-primary)' }}>{m.twVal}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{m.lwVal}</td>
                    <td><ChangeBadge curr={m.curr} prev={m.prev} invertSign={m.invertSign} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        {/* Psychology Summary */}
        <ChartCard title="Weekly Psychology Summary">
          <div className="comparison-grid" style={{ marginBottom: 0 }}>
            {[
              { label: 'Dominant Emotion', twVal: topEmotion(twTrades), lwVal: topEmotion(lwTrades) },
              { label: 'FOMO Trades',      twVal: emotionCount(twTrades, 'FOMO'),    lwVal: emotionCount(lwTrades, 'FOMO'),    curr: -emotionCount(twTrades,'FOMO'),    prev: -emotionCount(lwTrades,'FOMO') },
              { label: 'Revenge Trades',   twVal: emotionCount(twTrades, 'Revenge'), lwVal: emotionCount(lwTrades, 'Revenge'), curr: -emotionCount(twTrades,'Revenge'), prev: -emotionCount(lwTrades,'Revenge') },
              { label: 'Discipline Score', twVal: `${calcDiscipline(twTrades)}/100`, lwVal: `${calcDiscipline(lwTrades)}/100`, curr: calcDiscipline(twTrades), prev: calcDiscipline(lwTrades) },
            ].map(m => (
              <div key={m.label} className="comparison-metric" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span className="metric-label" style={{ minWidth: 130 }}>{m.label}</span>
                <span style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{m.twVal}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>vs</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{m.lwVal}</span>
                  {m.curr !== undefined && <ChangeBadge curr={m.curr} prev={m.prev} />}
                </span>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Best / Worst Day Cards */}
        <div className="comparison-grid">
          <div className="comparison-card" style={{ background: 'var(--success-bg)', border: '1px solid var(--profit)' }}>
            <div className="comparison-card-header" style={{ color: 'var(--profit)' }}>Best Day This Week</div>
            {bestDay ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.profit }}>{sign(bestDay[1])}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>{getDayName(bestDay[0])} · {twTrades.filter(t => t.date === bestDay[0]).length} trade(s)</div>
              </>
            ) : <div style={{ color: 'var(--text-muted)' }}>No trades this week</div>}
          </div>
          <div className="comparison-card" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)' }}>
            <div className="comparison-card-header" style={{ color: 'var(--loss)' }}>Worst Day This Week</div>
            {worstDay ? (
              <>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.loss }}>{sign(worstDay[1])}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>{getDayName(worstDay[0])} · {twTrades.filter(t => t.date === worstDay[0]).length} trade(s)</div>
              </>
            ) : <div style={{ color: 'var(--text-muted)' }}>No trades this week</div>}
          </div>
        </div>
      </div>
    );
  };

  // ── TAB: Monthly Comparison ──────────────────────────────────────────────
  const renderMonthly = () => {
    const now = new Date();
    const targetDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year  = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const monthName = targetDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

    const prevDate  = new Date(year, month - 1, 1);
    const prevYear  = prevDate.getFullYear();
    const prevMonth = prevDate.getMonth();

    const tmTrades = getMonthTrades(trades, year, month);
    const lmTrades = getMonthTrades(trades, prevYear, prevMonth);
    const tm = calcStats(tmTrades);
    const lm = calcStats(lmTrades);

    // Calendar heatmap
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDow    = new Date(year, month, 1).getDay(); // 0=Sun
    const dayPnl = {};
    const dayCnt = {};
    tmTrades.forEach(t => {
      const k = t.date;
      dayPnl[k] = (dayPnl[k] || 0) + (t.pnl || 0);
      dayCnt[k] = (dayCnt[k] || 0) + 1;
    });
    const calendarCells = [];
    // Leading blanks (Mon-based)
    const leadBlanks = (firstDow === 0 ? 6 : firstDow - 1);
    for (let i = 0; i < leadBlanks; i++) calendarCells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const pnl = dayPnl[dateStr];
      calendarCells.push({ day: d, date: dateStr, pnl, count: dayCnt[dateStr] || 0 });
    }

    // Week-by-week (4 weeks) for chart
    const getWeekOfMonth = (weekIdx, yr, mo) => {
      const start = new Date(yr, mo, 1 + weekIdx * 7);
      const end   = new Date(yr, mo, Math.min(7 + weekIdx * 7, new Date(yr, mo + 1, 0).getDate()));
      end.setHours(23, 59, 59, 999);
      return getMonthTrades(trades, yr, mo).filter(t => {
        const d = new Date(t.date);
        return d >= start && d <= end;
      });
    };
    const weeklyChartData = [0, 1, 2, 3].map(i => ({
      week: `Wk ${i + 1}`,
      'This Month': Math.round(calcStats(getWeekOfMonth(i, year, month)).pnl),
      'Last Month': Math.round(calcStats(getWeekOfMonth(i, prevYear, prevMonth)).pnl),
    }));

    // Best / worst week
    const weekStats = [0, 1, 2, 3].map(i => {
      const wt = getWeekOfMonth(i, year, month);
      return { idx: i + 1, ...calcStats(wt) };
    });
    const bestWeek  = weekStats.reduce((a, b) => b.pnl > a.pnl ? b : a);
    const worstWeek = weekStats.reduce((a, b) => b.pnl < a.pnl ? b : a);

    // Emotion pie
    const EMOTION_COLORS = { Calm: C.profit, Confident: C.profit, FOMO: C.orange, Greedy: C.loss, Fearful: C.purple, Revenge: '#ef4444', Anxious: C.orange, Neutral: C.muted };
    const emotionMap = {};
    tmTrades.forEach(t => {
      const e = t.emotion || 'Neutral';
      emotionMap[e] = (emotionMap[e] || 0) + 1;
    });
    const pieData = Object.entries(emotionMap).map(([name, value]) => ({ name, value }));

    // 6-month trend
    const sixMonthData = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(year, month - 5 + i, 1);
      const tr = getMonthTrades(trades, d.getFullYear(), d.getMonth());
      return {
        name: d.toLocaleDateString('en-IN', { month: 'short' }),
        pnl:  Math.round(tr.reduce((s, t) => s + (t.pnl || 0), 0)),
      };
    });

    // Metrics table
    const avgDailyPnl = t => {
      const days = new Set(t.map(x => x.date)).size;
      return days ? t.reduce((s, x) => s + (x.pnl || 0), 0) / days : 0;
    };
    const monthMetrics = [
      { label: 'Total P&L',       tmVal: sign(tm.pnl),  lmVal: sign(lm.pnl),  curr: tm.pnl,  prev: lm.pnl,  tmColor: tm.pnl >= 0 ? C.profit : C.loss },
      { label: 'Win Rate',        tmVal: `${tm.winRate.toFixed(1)}%`,   lmVal: `${lm.winRate.toFixed(1)}%`,   curr: tm.winRate, prev: lm.winRate },
      { label: 'Total Trades',    tmVal: tm.trades, lmVal: lm.trades, curr: tm.trades, prev: lm.trades },
      { label: 'Trading Days',    tmVal: new Set(tmTrades.map(t => t.date)).size, lmVal: new Set(lmTrades.map(t => t.date)).size },
      { label: 'Profit Factor',   tmVal: isFinite(tm.profitFactor) ? tm.profitFactor.toFixed(2) : '∞', lmVal: isFinite(lm.profitFactor) ? lm.profitFactor.toFixed(2) : '∞', curr: isFinite(tm.profitFactor) ? tm.profitFactor : 0, prev: isFinite(lm.profitFactor) ? lm.profitFactor : 0 },
      { label: 'Avg Daily P&L',   tmVal: sign(avgDailyPnl(tmTrades)), lmVal: sign(avgDailyPnl(lmTrades)), curr: avgDailyPnl(tmTrades), prev: avgDailyPnl(lmTrades) },
      { label: 'Best Day P&L',    tmVal: sign(tm.bestTrade),  lmVal: sign(lm.bestTrade),  curr: tm.bestTrade,  prev: lm.bestTrade },
      { label: 'Worst Day P&L',   tmVal: sign(tm.worstTrade), lmVal: sign(lm.worstTrade), curr: tm.worstTrade, prev: lm.worstTrade, invertSign: true },
      { label: 'Avg Win',         tmVal: sign(tm.avgWin),     lmVal: sign(lm.avgWin),     curr: tm.avgWin,  prev: lm.avgWin },
      { label: 'Avg Loss',        tmVal: `₹${fmt(tm.avgLoss)}`, lmVal: `₹${fmt(lm.avgLoss)}`, curr: -tm.avgLoss, prev: -lm.avgLoss },
      { label: 'Max Drawdown',    tmVal: `₹${fmt(calcMaxDrawdown(tmTrades))}`, lmVal: `₹${fmt(calcMaxDrawdown(lmTrades))}`, curr: -calcMaxDrawdown(tmTrades), prev: -calcMaxDrawdown(lmTrades), invertSign: true },
      { label: 'FOMO Trades',     tmVal: tmTrades.filter(t => t.emotion === 'FOMO').length, lmVal: lmTrades.filter(t => t.emotion === 'FOMO').length, curr: -tmTrades.filter(t => t.emotion==='FOMO').length, prev: -lmTrades.filter(t => t.emotion==='FOMO').length },
    ];

    return (
      <div className="rp-section">
        {/* Month Selector */}
        <div className="week-selector">
          <button onClick={() => setMonthOffset(o => o - 1)}>◀ Prev</button>
          <span className="week-label">{monthName}</span>
          <button onClick={() => setMonthOffset(o => Math.min(o + 1, 0))} disabled={monthOffset >= 0} style={{ opacity: monthOffset >= 0 ? 0.4 : 1 }}>Next ▶</button>
        </div>

        {/* Comparison Cards */}
        <div className="comparison-grid">
          <div className="comparison-card current">
            <div className="comparison-card-header">This Month</div>
            {[
              { label: 'Total P&L',     val: sign(tm.pnl),   color: tm.pnl >= 0 ? C.profit : C.loss },
              { label: 'Total Trades',  val: tm.trades },
              { label: 'Win Rate',      val: `${tm.winRate.toFixed(1)}%`, color: tm.winRate >= 50 ? C.profit : C.loss },
              { label: 'Profit Factor', val: isFinite(tm.profitFactor) ? tm.profitFactor.toFixed(2) : '∞' },
              { label: 'Trading Days',  val: new Set(tmTrades.map(t => t.date)).size },
              { label: 'Best Week',     val: `Week ${bestWeek.idx} · ${sign(bestWeek.pnl)}`, color: C.profit },
              { label: 'Worst Week',    val: `Week ${worstWeek.idx} · ${sign(worstWeek.pnl)}`, color: C.loss },
              { label: 'Avg Daily P&L', val: sign(avgDailyPnl(tmTrades)) },
            ].map(m => (
              <div key={m.label} className="comparison-metric">
                <span className="metric-label">{m.label}</span>
                <span className="metric-value" style={{ color: m.color || 'var(--text-primary)' }}>{m.val}</span>
              </div>
            ))}
          </div>

          <div className="comparison-card">
            <div className="comparison-card-header">Last Month</div>
            {[
              { label: 'Total P&L',     val: sign(lm.pnl),   color: lm.pnl >= 0 ? C.profit : C.loss },
              { label: 'Total Trades',  val: lm.trades },
              { label: 'Win Rate',      val: `${lm.winRate.toFixed(1)}%`, color: lm.winRate >= 50 ? C.profit : C.loss },
              { label: 'Profit Factor', val: isFinite(lm.profitFactor) ? lm.profitFactor.toFixed(2) : '∞' },
              { label: 'Trading Days',  val: new Set(lmTrades.map(t => t.date)).size },
              { label: 'Avg Daily P&L', val: sign(avgDailyPnl(lmTrades)) },
            ].map(m => (
              <div key={m.label} className="comparison-metric">
                <span className="metric-label">{m.label}</span>
                <span className="metric-value" style={{ color: m.color || 'var(--text-primary)' }}>{m.val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar Heatmap */}
        <ChartCard title={`P&L Calendar — ${monthName}`}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', paddingBottom: 4, fontWeight: 600 }}>{d}</div>
            ))}
          </div>
          <div className="calendar-grid">
            {calendarCells.map((cell, i) => {
              if (!cell) return <div key={`blank-${i}`} />;
              const hasTrade = cell.pnl !== undefined;
              const isProfit = cell.pnl > 0;
              const isLoss   = cell.pnl < 0;
              let cls = 'calendar-day no-trade';
              if (hasTrade && isProfit) cls = 'calendar-day profit';
              if (hasTrade && isLoss)   cls = 'calendar-day loss';
              return (
                <div
                  key={cell.date}
                  className={cls}
                  title={hasTrade ? `${cell.date}: ${sign(cell.pnl)} (${cell.count} trade${cell.count > 1 ? 's' : ''})` : cell.date}
                >
                  {cell.day}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
            <span style={{ color: C.profit }}>● Profit Day</span>
            <span style={{ color: C.loss }}>● Loss Day</span>
            <span style={{ color: 'var(--text-muted)' }}>● No Trades</span>
          </div>
        </ChartCard>

        {/* Week by Week Chart */}
        <ChartCard title="Week by Week P&L — This Month vs Last Month">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={weeklyChartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="week" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
              <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
              <Tooltip content={<Tip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }} />
              <Bar dataKey="This Month" fill={C.profit} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Last Month" fill={C.muted}  radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Monthly Metrics Table */}
        <ChartCard title="Monthly Metrics Comparison">
          <div style={{ overflowX: 'auto' }}>
            <table className="rp-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>This Month</th>
                  <th>Last Month</th>
                  <th>Change</th>
                </tr>
              </thead>
              <tbody>
                {monthMetrics.map((m, i) => (
                  <tr key={m.label} style={{ background: i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-primary)' }}>
                    <td style={{ color: 'var(--text-secondary)' }}>{m.label}</td>
                    <td style={{ fontWeight: 700, color: m.tmColor || 'var(--text-primary)' }}>{m.tmVal}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{m.lmVal}</td>
                    <td><ChangeBadge curr={m.curr} prev={m.prev} invertSign={m.invertSign} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        {/* Emotion Pie */}
        {pieData.length > 0 && (
          <div className="rp-chart-row">
            <ChartCard title={`Emotion Breakdown — ${monthName}`}>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={EMOTION_COLORS[entry.name] || C.blue} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="rp-pie-legend" style={{ flexWrap: 'wrap', gap: '8px 16px' }}>
                {pieData.map(e => (
                  <span key={e.name} style={{ color: EMOTION_COLORS[e.name] || C.blue }}>
                    ● {e.name}: {e.value}
                  </span>
                ))}
              </div>
            </ChartCard>

            {/* Best / Worst Week */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="comparison-card" style={{ background: 'var(--success-bg)', border: '1px solid var(--profit)', flex: 1 }}>
                <div className="comparison-card-header" style={{ color: 'var(--profit)' }}>Best Week</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.profit }}>{sign(bestWeek.pnl)}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Week {bestWeek.idx} · {bestWeek.trades} trade(s) · {bestWeek.winRate.toFixed(0)}% WR</div>
              </div>
              <div className="comparison-card" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', flex: 1 }}>
                <div className="comparison-card-header" style={{ color: 'var(--loss)' }}>Worst Week</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.loss }}>{sign(worstWeek.pnl)}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Week {worstWeek.idx} · {worstWeek.trades} trade(s) · {worstWeek.winRate.toFixed(0)}% WR</div>
              </div>
            </div>
          </div>
        )}

        {/* 6-Month Trend */}
        <ChartCard title="6-Month P&L Trend">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={sixMonthData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="sixMonthGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={C.profit} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={C.profit} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
              <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} tickFormatter={v => `₹${fmt(v)}`} />
              <Tooltip content={<Tip />} />
              <Area type="monotone" dataKey="pnl" name="Monthly P&L" stroke={C.profit} fill="url(#sixMonthGrad)" strokeWidth={2} dot={{ r: 4, fill: C.profit }} activeDot={{ r: 6 }} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="rp-page">
      <div className="rp-tab-bar">
        {TABS.map(t => {
          const locked = !can(t.feature);
          return (
            <button
              key={t.id}
              className={`rp-tab${tab === t.id ? ' active' : ''}${locked ? ' locked' : ''}`}
              onClick={() => { if (!locked) setTab(t.id); else setTab(t.id); }}
              style={{ opacity: locked ? 0.6 : 1, cursor: 'pointer' }}
            >
              {locked && '🔒 '}{t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && renderOverview()}
      {tab === 'weekly'   && (!can('reportsWeekly')   ? <UpgradePrompt requiredPlan="pro" feature="Weekly Report Pro plan mein available hai." /> : renderWeekly())}
      {tab === 'monthly'  && (!can('reportsMonthly')  ? <UpgradePrompt requiredPlan="pro" feature="Monthly Report Pro plan mein available hai." /> : renderMonthly())}
      {tab === 'perf'     && (!can('reportsPerformance') ? <UpgradePrompt requiredPlan="pro" feature="Advanced Performance reports Pro plan mein available hain." /> : renderPerformance())}
      {tab === 'psych'    && (!can('reportsPsychology')  ? <UpgradePrompt requiredPlan="pro" feature="Psychology reports Pro plan mein available hain." /> : renderPsychology())}
      {tab === 'risk'     && (!can('reportsRisk')     ? <UpgradePrompt requiredPlan="pro" feature="Risk Report Pro plan mein available hai." /> : renderRisk())}
      {tab === 'symbols'  && (!can('reportsSymbol')   ? <UpgradePrompt requiredPlan="pro" feature="Symbol Analysis Pro plan mein available hai." /> : renderSymbols())}
      {tab === 'export'   && (!can('reportsExport')   ? <UpgradePrompt requiredPlan="pro" feature="Export feature Pro plan mein available hai." /> : renderExport())}
    </div>
  );
};

export default Reports;
